import cx from 'clsx';
import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import Button from '@components/uikit/Button/Button';

import { calcSensorHex } from '@helpers/helper';

import useTranslate from '@hooks/useTranslate';
import useTranslation from '@hooks/useTranslation';

import type { IInstallationPoint } from '@model/installationPoint';

import EmptyFilterBlock from '@pages/Sensors/components/EmptyFilterBlock/EmptyFilterBlock';
import { selectSensorWithAi } from '@pages/Sensors/store/useSensorStore';

import { SENSOR_STATUSES } from '../../constants/sensorStatuses';
import { getFilteredSensorData } from '../../helpers/getFilteredSensorData';
import type { ISensorListEquipment } from '../../model/sensorListEquipment';
import style from './style.module.scss';

interface IProps {
  equipment?: ISensorListEquipment;
}

const SensorListTableBody: React.FC<IProps> = ({ equipment }) => {
  const { t } = useTranslation();
  const { translateField } = useTranslate();
  const { customerId } = useParams();
  const { pathname } = useLocation();

  const navigate = useNavigate();

  const sensorWithAi = selectSensorWithAi();

  const equipmentIsDeactivated = !!equipment?.is_deactivated;
  const tableData = getFilteredSensorData(equipment?.installationPoints || [], sensorWithAi);

  const getSensorStatus = (item: IInstallationPoint) => {
    if ((!item.sensor && !item.sensor_id && item.point_type !== 'customer') || equipmentIsDeactivated) {
      return 'Inactive';
    }

    return SENSOR_STATUSES[item.activeActionItems?.length ? 'WARNING' : 'NORMAL'];
  };

  return (
    <div className={style.sensorListTableBody}>
      {tableData.map((tableDataRow) => (
        <div
          onClick={(e) => {
            navigate(
              `/customer/${customerId}/equipment-details/${
                tableDataRow.point_type === 'tach' ? 'tachometer' : 'sensor'
              }/${tableDataRow.id}`,
              { state: { prevPage: pathname } },
            );
          }}
          key={tableDataRow.id}
          className={cx(style.sensorListTableBodyRow, {
            [style.unselected]: getSensorStatus(tableDataRow) === 'Inactive',
          })}
        >
          <div className={style.sensorListTableBodyName} data-qa={'sensor-description'}>
            {translateField({ item: tableDataRow })}
          </div>
          <div className={style.sensorListTableBodyCode} data-qa={'sensor-hex'}>
            {calcSensorHex(tableDataRow.sensor_id, tableDataRow.hardware_id)}
          </div>
          <div className={style.sensorListTableBodyStatus} data-qa={'sensor-status'}>
            {t(getSensorStatus(tableDataRow))}
          </div>
          <div className={style.sensorListTableBodyLinks}>
            {tableDataRow.activeActionItems?.map((item) => (
              <div key={item.id} className={style.sensorListTableBodyLinksItem}>
                <div data-qa={'sensor-type'}>
                  {item.type === 'waitesWireless' ? t('Hardware') : t('Regular')}
                </div>
                <Link
                  className={style.sensorListTableBodyBtn}
                  to={`/customer/${customerId}/ai/${item.id}`}
                  state={{ prevPage: pathname }}
                  data-qa={'sensor-links'}
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('View action item')}
                </Link>
              </div>
            ))}
          </div>
        </div>
      ))}
      {tableData.length < 1 && (
        <div className={style.sensorListTableBodyEmpty}>
          <EmptyFilterBlock mainText={t('No action items were found')} />
        </div>
      )}
    </div>
  );
};

export default SensorListTableBody;
